import type {
  DatedFinanceResult,
  DatedLedgerEntry,
} from "./dated-finance-types";
import type { DatedFlow, OwnershipInput } from "./dated-ownership";
import type { Scenario } from "./types";

export type LeasePaymentSource = "scenario-rent" | "manual";
export type ResolvedOwnershipInput = OwnershipInput & {
  leasePaymentSource?: LeasePaymentSource;
};

function cloneFlows(flows: readonly DatedFlow[]): DatedFlow[] {
  return flows.map((flow) => ({ ...flow }));
}

/**
 * Produces an ownership input without mutating the saved scenario. Manual lease rows win when
 * present. Otherwise, dated ledger rows are the sole rent source: they carry resolved milestone
 * dates and already respect rent-commencement rules.
 */
export function resolveScenarioOwnershipInput(
  scenario: Scenario,
  finance?: DatedFinanceResult,
): ResolvedOwnershipInput | null {
  const ownership = scenario.datedOwnership;
  if (!ownership) return null;

  const copied: ResolvedOwnershipInput = {
    ...ownership,
    leasePayments: cloneFlows(ownership.leasePayments),
    ownerAcquisitionAndBuildCosts: cloneFlows(
      ownership.ownerAcquisitionAndBuildCosts,
    ),
    ownerLifecycle:
      ownership.ownerLifecycle == null
        ? ownership.ownerLifecycle
        : {
            ...ownership.ownerLifecycle,
            categories: ownership.ownerLifecycle.categories.map((category) => ({
              ...category,
            })),
          },
    financing:
      ownership.financing == null
        ? ownership.financing
        : {
            draws: cloneFlows(ownership.financing.draws),
            repayments: cloneFlows(ownership.financing.repayments),
            interestPayments: cloneFlows(ownership.financing.interestPayments),
            fees: cloneFlows(ownership.financing.fees),
          },
  };

  const explicitSource = (ownership as ResolvedOwnershipInput)
    .leasePaymentSource;
  if (copied.leasePayments.length > 0 || explicitSource === "manual") {
    return { ...copied, leasePaymentSource: "manual" };
  }

  return {
    ...copied,
    leasePayments: sourceRentPayments(finance?.ledger ?? []),
    leasePaymentSource: "scenario-rent",
  };
}

/**
 * Positive landlord rent is a lease cost. A rent-credit contribution has project-negative and
 * operator-positive signs, so it reduces that cost. Other contributions and all operator P&L
 * entries deliberately remain outside this lease-vs-own series.
 */
function sourceRentPayments(ledger: readonly DatedLedgerEntry[]): DatedFlow[] {
  const byDate = new Map<string, number>();
  for (const entry of ledger) {
    const landlordRent =
      entry.category === "landlord-rent" && entry.project > 0;
    const rentCredit =
      entry.category === "contribution" &&
      entry.project < 0 &&
      entry.operator > 0;
    if (!landlordRent && !rentCredit) continue;
    byDate.set(entry.date, (byDate.get(entry.date) ?? 0) + entry.project);
  }
  return [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, amount]) => ({
      date,
      amount,
      label: amount < 0 ? "Dated rent credit / refund" : "Dated landlord rent",
    }));
}
